import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';

const SHOW_DELAY_MS = 100;
const HIDE_DELAY_MS = 100;

@Component({
  selector: 'app-tooltip-overlay',
  template: `<div
    data-testid="tooltip-overlay"
    class="bg-neutral text-neutral-content text-xs shadow-sm max-h-[30vh] w-max max-w-[30vw] min-w-0 overflow-auto rounded-btn px-2 py-1 break-words whitespace-pre-wrap"
  >
    {{ text() }}
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block max-w-[30vw] min-w-0' },
})
export class TooltipOverlayComponent {
  // vw, not %: the overlay pane shrink-wraps this element, so a percentage never resolves.
  readonly text = signal('');
}

@Directive({
  selector: '[appTooltip]',
})
export class TooltipDirective {
  readonly #overlay = inject(Overlay);

  readonly #elementRef = inject(ElementRef<HTMLElement>);
  readonly #destroyRef = inject(DestroyRef);
  #overlayRef: OverlayRef | null = null;

  #showTimeout: ReturnType<typeof setTimeout> | null = null;
  #hideTimeout: ReturnType<typeof setTimeout> | null = null;

  #onMouseEnter = (): void => {
    this.#cancelHide();
    this.#cancelShow();
    this.#showTimeout = setTimeout(() => this.#show(), SHOW_DELAY_MS);
  };

  #onMouseLeave = (): void => {
    this.#cancelShow();
    this.#scheduleHide();
  };

  #onOverlayMouseEnter = (): void => {
    this.#cancelHide();
  };

  #onOverlayMouseLeave = (): void => {
    this.#scheduleHide();
  };

  readonly appTooltip = input.required<string>();

  constructor() {
    const el = this.#elementRef.nativeElement;

    el.addEventListener('mouseenter', this.#onMouseEnter);
    el.addEventListener('mouseleave', this.#onMouseLeave);

    this.#destroyRef.onDestroy(() => {
      el.removeEventListener('mouseenter', this.#onMouseEnter);
      el.removeEventListener('mouseleave', this.#onMouseLeave);
      this.#cancelShow();
      this.#cancelHide();
      this.#hide();
    });
  }

  #show(): void {
    this.#showTimeout = null;
    const text = this.appTooltip();
    // close() detaches on scroll and leaves the OverlayRef. A detached ref is not showing.
    if (this.#overlayRef?.hasAttached()) {
      return;
    }
    if (this.#overlayRef) {
      this.#hide();
    }
    if (!text) {
      return;
    }

    this.#overlayRef = this.#overlay.create({
      hasBackdrop: false,
      positionStrategy: this.#overlay
        .position()
        .flexibleConnectedTo(this.#elementRef)
        .withPositions([
          // Grow left from the cell's right edge. A centered box on a right-hand
          // column hangs off the viewport.
          {
            offsetY: 4,
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            offsetY: -4,
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
          {
            offsetY: 4,
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            offsetY: -4,
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ])
        .withPush(true),
      scrollStrategy: this.#overlay.scrollStrategies.close(),
      // Top layer, like the popover dropdowns: otherwise a tooltip inside one renders behind it.
      usePopover: true,
    });

    const portal = new ComponentPortal(TooltipOverlayComponent);
    const ref = this.#overlayRef.attach(portal);
    ref.instance.text.set(text);

    const overlayElement = this.#overlayRef.overlayElement;
    overlayElement.addEventListener('mouseenter', this.#onOverlayMouseEnter);
    overlayElement.addEventListener('mouseleave', this.#onOverlayMouseLeave);
  }

  #scheduleHide(): void {
    this.#cancelHide();
    this.#hideTimeout = setTimeout(() => this.#hide(), HIDE_DELAY_MS);
  }

  #cancelShow(): void {
    if (!this.#showTimeout) return;
    clearTimeout(this.#showTimeout);
    this.#showTimeout = null;
  }

  #cancelHide(): void {
    if (!this.#hideTimeout) return;
    clearTimeout(this.#hideTimeout);
    this.#hideTimeout = null;
  }

  #hide(): void {
    this.#hideTimeout = null;
    const overlayElement = this.#overlayRef?.overlayElement;
    overlayElement?.removeEventListener(
      'mouseenter',
      this.#onOverlayMouseEnter
    );
    overlayElement?.removeEventListener(
      'mouseleave',
      this.#onOverlayMouseLeave
    );
    this.#overlayRef?.dispose();
    this.#overlayRef = null;
  }
}
